import React, { useState, useEffect } from 'react';
import { Form, Button, Alert } from '@/components/ui';
import { workIdService, type WorkIdDetail } from '../../../services/workIdService';
import ResponsiveModal from '../../../components/ResponsiveModal';

interface WorkIdCancelModalProps {
  visible: boolean;
  onClose: () => void;
  workId: WorkIdDetail | null;
  onSuccess: () => void;
}

const WorkIdCancelModal: React.FC<WorkIdCancelModalProps> = ({
  visible,
  onClose,
  workId,
  onSuccess,
}) => {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (visible) {
      setReason('');
      setError(null);
      setValidationError(null);
    }
  }, [visible, workId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!workId) return;

    const trimmed = reason.trim();
    if (!trimmed) {
      setValidationError('Reason is required.');
      return;
    }
    setValidationError(null);
    setError(null);
    setSubmitting(true);

    try {
      await workIdService.cancel(workId.id, trimmed);
      onClose();
      onSuccess();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to cancel work ID');
    } finally {
      setSubmitting(false);
    }
  };

  if (!workId) return null;

  const title = `Cancel Work ID - ${workId.workIdentificationId ?? workId.id}`;

  return (
    <ResponsiveModal
      isOpen={visible}
      onClose={onClose}
      title={title}
      size="medium"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Close
          </Button>
          <Button
            type="button"
            variant="danger"
            onClick={handleSubmit}
            disabled={submitting || !reason.trim()}
          >
            {submitting ? 'Cancelling…' : 'Cancel Work ID'}
          </Button>
        </>
      }
    >
      <Form onSubmit={handleSubmit}>
        {error && (
          <Alert variant="danger">
            {error}
          </Alert>
        )}

        <p className="tw-text-content-muted tw-mb-3">
          Cancelled Work IDs can be reopened (status returns to NEW, requires approval again).
        </p>

        <Form.Group className="tw-mb-0">
          <Form.Label>Reason (required)</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              setValidationError(null);
            }}
            placeholder="Enter reason for cancellation"
            isInvalid={!!validationError}
          />
          {validationError && (
            <Form.Control.Feedback type="invalid">
              {validationError}
            </Form.Control.Feedback>
          )}
        </Form.Group>
      </Form>
    </ResponsiveModal>
  );
};

export default WorkIdCancelModal;
